import React, { useEffect, useMemo, useRef, useState } from "react";
import { Course } from "./types";
import { loadCourses, SavedCourse, formatCourseLocation } from "./coursesDb";
import CourseAddModal from "./CourseAddModal";
import CourseImport from "./CourseImport";

interface Props {
  // Static "starter" catalog (e.g. mockCourses). Merged at render time
  // with whatever the user has saved locally — both flow through the
  // same search list. When a backend course store lands later, this
  // prop can either carry that fetch's result or be removed entirely.
  courses: Course[];
  onSelect?: (course: Course) => void;
  // Admin/curation variant. When provided, each row's primary button becomes a
  // red "Delete" (calling this) instead of "Select" — used by the CGAdmin
  // course-catalog console. The parent owns the confirm dialog + deletion.
  onDelete?: (course: Course) => void;
  // Admin/curation variant. When provided, each row gets an "Enrich" button that
  // asks the Course Enrichment Service for suggested improvements (the parent
  // owns the call + review modal). `enrichingId` marks the row currently running.
  onEnrich?: (course: Course) => void;
  enrichingId?: string | null;
  // Hide the "+ Add Course" / "Import from Scorecard" affordances. Used by the
  // tournament wizard, where building the course library is a player-side task
  // only — the admin just picks an existing course.
  hideAddActions?: boolean;
  // Show the whole catalog (alphabetical) before any search, instead of the
  // "start typing" empty state. The CGAdmin console uses this so the full
  // course list is visible up front; the player Play screen leaves it off.
  showAllWhenEmpty?: boolean;
  // Overrides the "Select a course" heading (e.g. "Course catalog" in admin).
  title?: string;
  // When false, don't merge the local (localStorage) course layer — the list
  // is exactly `courses`. The CGAdmin console sets this so it shows only the
  // shared backend catalog. Defaults true (player app: seed + local edits).
  mergeLocal?: boolean;
  // Persistence override for the add / view-edit / import card editors. When
  // provided, saves go through this (shared backend catalog) instead of the
  // local coursesDb. Threaded into every CourseAddModal this screen opens.
  persist?: (course: Course, mode: "create" | "update") => Promise<void>;
  // Extra content rendered beside the heading (e.g. the CGAdmin review bell).
  headerRight?: React.ReactNode;
  // Favourites (player quick-picks). When onToggleFavorite is provided, each
  // row shows a star toggle and — while idle (no search) — the favourited
  // courses appear in a strip under the search bar for one-tap play.
  favorites?: string[];
  onToggleFavorite?: (course: Course) => void;
  // Server-search mode. When provided, the list is fetched from the backend
  // per query (debounced) with pagination + server-side Country/Region filters,
  // instead of filtering the in-memory `courses` prop — this is what scales
  // past a few hundred courses. `loadFacets` supplies the filter dropdowns.
  serverSearch?: (params: {
    q: string;
    country: string;
    region: string;
    offset: number;
    limit: number;
  }) => Promise<{ courses: Course[]; hasMore: boolean; total?: number }>;
  loadFacets?: (
    country: string,
  ) => Promise<{ countries: string[]; regions: string[] }>;
  // Bump to force a re-query in server mode (e.g. after the parent deletes or
  // approves a course externally).
  reloadKey?: number;
}

// Course picker. Empty until the user types — courses surface only on
// active search, simulating an org-wide database query. Two side
// affordances let the user grow the local database: + Add Course
// (manual entry) and Import from Scorecard (image → mock extract →
// editable verify → save).
export default function CourseSelect({
  courses,
  onSelect,
  onDelete,
  onEnrich,
  enrichingId,
  hideAddActions = false,
  showAllWhenEmpty = false,
  title = "Select a course",
  mergeLocal = true,
  persist,
  headerRight,
  favorites,
  onToggleFavorite,
  serverSearch,
  loadFacets,
  reloadKey,
}: Props) {
  const serverMode = !!serverSearch;
  const PAGE = 100;
  // Latest fn refs so the search/facets effects don't churn if the parent
  // recreates these callbacks each render.
  const serverSearchRef = useRef(serverSearch);
  serverSearchRef.current = serverSearch;
  const loadFacetsRef = useRef(loadFacets);
  loadFacetsRef.current = loadFacets;

  // Server-mode result state.
  const [results, setResults] = useState<Course[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [serverTotal, setServerTotal] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [serverFacets, setServerFacets] = useState<{
    countries: string[];
    regions: string[];
  }>({ countries: [], regions: [] });
  const [query, setQuery] = useState("");
  // Bumped after a save so the merged list re-reads from localStorage.
  // Cheaper than threading state through an external store.
  const [version, setVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  // Course whose card is being previewed (View card). Starts read-only;
  // the modal itself offers an Edit toggle.
  const [viewing, setViewing] = useState<Course | null>(null);
  // Advanced search — expandable Country + Region filters.
  const [advOpen, setAdvOpen] = useState(false);
  const [filterCountry, setFilterCountry] = useState("");
  const [filterRegion, setFilterRegion] = useState("");

  const allCourses = useMemo<Course[]>(() => {
    // Dedupe by id, saved courses overriding fixtures — so an edited
    // fixture (saved under the same id) replaces it rather than doubling.
    const map = new Map<string, Course>();
    for (const c of courses) map.set(c.id, c);
    if (mergeLocal) for (const c of loadCourses()) map.set(c.id, c);
    return [...map.values()];
  }, [courses, version, mergeLocal]);

  const trimmed = query.trim();
  const hasFilter = !!filterCountry || !!filterRegion;

  // Client-mode filter options — distinct values from the loaded catalog.
  // Region narrows to the chosen country so the pair can't form an empty combo.
  const clientCountryOptions = useMemo(
    () =>
      [...new Set(allCourses.map((c) => c.country).filter(Boolean))].sort() as string[],
    [allCourses],
  );
  const clientRegionOptions = useMemo(() => {
    const src = filterCountry
      ? allCourses.filter((c) => c.country === filterCountry)
      : allCourses;
    return [...new Set(src.map((c) => c.region).filter(Boolean))].sort() as string[];
  }, [allCourses, filterCountry]);

  // Client-mode filtered list (in-memory).
  const filtered = useMemo(() => {
    let list = allCourses;
    if (filterCountry) list = list.filter((c) => c.country === filterCountry);
    if (filterRegion) list = list.filter((c) => c.region === filterRegion);
    if (trimmed) {
      const q = trimmed.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q));
    } else if (!showAllWhenEmpty && !hasFilter) {
      // Search-first: idle with no filter shows nothing (favourites cover it).
      return [];
    }
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [allCourses, trimmed, showAllWhenEmpty, hasFilter, filterCountry, filterRegion]);

  // ── Server mode: fetch a page from the backend (debounced) ────────────────
  // Query only when there's something to fetch: a search term, an active
  // filter, or an admin browse (showAllWhenEmpty). Idle player = favourites.
  const shouldQuery = !!trimmed || hasFilter || showAllWhenEmpty;
  useEffect(() => {
    if (!serverMode) return;
    if (!shouldQuery) {
      setResults([]);
      setHasMore(false);
      setServerTotal(null);
      setOffset(0);
      return;
    }
    let alive = true;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await serverSearchRef.current!({
          q: trimmed,
          country: filterCountry,
          region: filterRegion,
          offset: 0,
          limit: PAGE,
        });
        if (!alive) return;
        setResults(r.courses);
        setHasMore(r.hasMore);
        setServerTotal(r.total ?? r.courses.length);
        setOffset(0);
      } catch {
        if (alive) {
          setResults([]);
          setHasMore(false);
          setServerTotal(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [
    serverMode,
    shouldQuery,
    trimmed,
    filterCountry,
    filterRegion,
    version,
    reloadKey,
  ]);

  // Facet dropdowns (server mode) — reload regions when the country changes.
  useEffect(() => {
    if (!serverMode || !loadFacetsRef.current) return;
    let alive = true;
    loadFacetsRef
      .current(filterCountry)
      .then((f) => {
        if (alive) setServerFacets(f);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [serverMode, filterCountry]);

  const loadMore = async () => {
    if (!serverMode || loading) return;
    const next = offset + PAGE;
    setLoading(true);
    try {
      const r = await serverSearchRef.current!({
        q: trimmed,
        country: filterCountry,
        region: filterRegion,
        offset: next,
        limit: PAGE,
      });
      setResults((prev) => [...prev, ...r.courses]);
      setHasMore(r.hasMore);
      setOffset(next);
    } catch {
      // keep what we have
    } finally {
      setLoading(false);
    }
  };

  const countryOptions = serverMode ? serverFacets.countries : clientCountryOptions;
  const regionOptions = serverMode ? serverFacets.regions : clientRegionOptions;
  const displayCourses = serverMode ? results : filtered;

  const refresh = () => setVersion((v) => v + 1);

  const favSet = useMemo(() => new Set(favorites ?? []), [favorites]);
  // Favourited courses resolved against the catalog, in favourite order;
  // ids that no longer resolve (deleted course) drop out quietly.
  const favoriteCourses = useMemo(() => {
    if (!favorites) return [];
    // Resolve against the passed catalog AND the current server results, so a
    // favourite shows even in server mode (parent seeds favourite courses via
    // the `courses` prop).
    const byId = new Map<string, Course>();
    for (const c of allCourses) byId.set(c.id, c);
    for (const c of results) byId.set(c.id, c);
    return favorites
      .map((id) => byId.get(id))
      .filter((c): c is Course => !!c);
  }, [favorites, allCourses, results]);

  const favStar = (c: Course) =>
    onToggleFavorite ? (
      <button
        type="button"
        className={`cg-fav-star${favSet.has(c.id) ? " cg-fav-star-on" : ""}`}
        onClick={() => onToggleFavorite(c)}
        aria-label={
          favSet.has(c.id)
            ? `Remove ${c.name} from favourites`
            : `Add ${c.name} to favourites`
        }
        title={favSet.has(c.id) ? "Remove from favourites" : "Add to favourites"}
      >
        {favSet.has(c.id) ? "★" : "☆"}
      </button>
    ) : null;

  if (importing) {
    return (
      <CourseImport
        persist={persist}
        onSaved={refresh}
        onClose={() => setImporting(false)}
      />
    );
  }

  return (
    <div className="cg-select">
      <div className="cg-select-titlerow">
        <h2 className="cg-select-title">
          {title}
          {showAllWhenEmpty &&
            ` (${
              serverMode && serverTotal !== null
                ? serverTotal
                : `${displayCourses.length}${hasMore ? "+" : ""}`
            })`}
        </h2>
        {headerRight}
      </div>
      <input
        className="cg-search"
        type="text"
        placeholder="Start typing the name of your course…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />

      {/* Advanced search — Country + Region filters. */}
      <div className="cg-adv">
        <button
          type="button"
          className="cg-adv-toggle"
          onClick={() => setAdvOpen((v) => !v)}
          aria-expanded={advOpen}
        >
          Advanced search {advOpen ? "▲" : "▼"}
          {hasFilter && <span className="cg-adv-dot" aria-hidden />}
        </button>
        {advOpen && (
          <div className="cg-adv-panel">
            <label className="cg-adv-field">
              <span>Country</span>
              <select
                value={filterCountry}
                onChange={(e) => {
                  setFilterCountry(e.target.value);
                  setFilterRegion("");
                }}
              >
                <option value="">All countries</option>
                {countryOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="cg-adv-field">
              <span>Region</span>
              <select
                value={filterRegion}
                onChange={(e) => setFilterRegion(e.target.value)}
              >
                <option value="">All regions</option>
                {regionOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            {hasFilter && (
              <button
                type="button"
                className="cg-btn cg-btn-link cg-adv-clear"
                onClick={() => {
                  setFilterCountry("");
                  setFilterRegion("");
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Favourites quick-picks — shown while idle (no search, no filter). Tap
          a row to play; tap the star to remove. */}
      {onToggleFavorite &&
        favoriteCourses.length > 0 &&
        !trimmed &&
        !hasFilter && (
        <div className="cg-fav-strip">
          <div className="cg-fav-label">Favourites</div>
          <ul className="cg-course-list">
            {favoriteCourses.map((c) => (
              <li key={`fav-${c.id}`} className="cg-course-row">
                {favStar(c)}
                <button
                  type="button"
                  className="cg-course-info cg-course-info-tap"
                  onClick={() => onSelect?.(c)}
                  aria-label={`Play ${c.name}`}
                >
                  <div className="cg-course-name">{c.name}</div>
                  {formatCourseLocation(c) && (
                    <div className="cg-course-loc">
                      {formatCourseLocation(c)}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {displayCourses.length === 0 ? (
        // No idle "start typing" hint — the placeholder says it, and it would
        // shove favourites down the page. Only speak up when a search / filter
        // genuinely returned nothing (or an admin browse hits an empty store).
        loading ? (
          <p className="cg-select-hint">Searching…</p>
        ) : trimmed || hasFilter ? (
          <p className="cg-select-hint">
            {trimmed
              ? `No courses match "${trimmed}".`
              : "No courses match those filters."}
          </p>
        ) : showAllWhenEmpty ? (
          <p className="cg-select-hint">No courses in the catalog yet.</p>
        ) : null
      ) : (
        <ul className="cg-course-list">
          {displayCourses.map((c) => (
            <li key={c.id} className="cg-course-row">
              {favStar(c)}
              <div className="cg-course-info">
                <div className="cg-course-name">{c.name}</div>
                {formatCourseLocation(c) && (
                  <div className="cg-course-loc">{formatCourseLocation(c)}</div>
                )}
                <div className="cg-course-meta">
                  {c.holes.length} holes • Par{" "}
                  {c.holes.reduce((s, h) => s + h.par, 0)}
                </div>
              </div>
              <div className="cg-course-row-actions">
                <button
                  type="button"
                  className="cg-btn cg-btn-ghost cg-course-view"
                  onClick={() => setViewing(c)}
                  aria-label={`View scorecard for ${c.name}`}
                >
                  View card
                </button>
                {onEnrich && (
                  <button
                    type="button"
                    className="cg-btn cg-btn-ghost cg-course-enrich"
                    onClick={() => onEnrich(c)}
                    disabled={enrichingId === c.id}
                    aria-label={`AI Verify ${c.name} against its official scorecard`}
                    title="Check the official scorecard online and suggest updates"
                  >
                    {enrichingId === c.id ? "Verifying…" : "AI Verify"}
                  </button>
                )}
                {onDelete ? (
                  <button
                    type="button"
                    className="cg-btn cg-btn-danger cg-course-select"
                    onClick={() => onDelete(c)}
                    aria-label={`Delete ${c.name}`}
                  >
                    Delete
                  </button>
                ) : (
                  <button
                    type="button"
                    className="cg-btn cg-btn-primary cg-course-select"
                    onClick={() => onSelect?.(c)}
                    aria-label={`Select ${c.name}`}
                  >
                    Select
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {serverMode && hasMore && (
        <button
          type="button"
          className="cg-btn cg-btn-ghost cg-load-more"
          onClick={loadMore}
          disabled={loading}
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}

      {!hideAddActions && (
        <div className="cg-course-actions">
          <button
            type="button"
            className="cg-btn cg-btn-primary cg-course-add-btn"
            onClick={() => setAdding(true)}
          >
            + Add Course
          </button>
          <button
            type="button"
            className="cg-btn cg-btn-ghost cg-course-import-btn"
            onClick={() => setImporting(true)}
          >
            Import from Scorecard
          </button>
        </div>
      )}

      {adding && (
        <CourseAddModal
          source="manual"
          persist={persist}
          onSaved={() => {
            setAdding(false);
            refresh();
          }}
          onClose={() => setAdding(false)}
        />
      )}

      {viewing && (
        <CourseAddModal
          title="Course card"
          readOnly
          persist={persist}
          courseId={viewing.id}
          source={(viewing as SavedCourse).source ?? "manual"}
          initialName={viewing.name}
          initialCity={viewing.city}
          initialRegion={viewing.region}
          initialCountry={viewing.country}
          initialLocation={viewing.location}
          initialHoles={viewing.holes}
          initialTees={viewing.tees}
          onSaved={() => {
            setViewing(null);
            refresh();
          }}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
